import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../../../components/Loading';
import PaginationBar from '../../../../components/PaginationBar/PaginationBar';
import usePermissions from '../../../../components/Menu/MenuItems/hooks/usePermissions';
import { listJobOpenings } from '../../../../services/hr/recruitment/jobOpeningsApi';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import { Container } from '../../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../../styles/layout/ActionBar';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field } from '../../../../styles/forms/Field';
import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { Table } from '../../../../styles/table/Table';
import { ClickableRow, TextLink } from '../../../../styles/ui/Link';
import { CtaButton } from '../../../../styles/ui/Button';

import JobPositionSelect from '../components/JobPositionSelect';
import JobOpeningStatusBadge from '../components/JobOpeningStatusBadge';
import { JOB_OPENING_STATUS_LABEL } from '../constants';
import { formatDateBr } from '../utils/format';

// ---------------------------------------------------------------------------
// RH · Recrutamento — Vagas / aberturas (handoff 0061 §4.2 / §5.3). Listagem
// URL-driven (navegacao.md cap.1): busca explícita, applied=1, replace:true, sem
// fetch no mount com URL limpa. SEM ordenação clicável (o backend não expõe
// order_by para /openings). Filtros: status e cargo.
// ---------------------------------------------------------------------------

const ITEMS_PER_PAGE = 20;

const DEFAULT_FILTERS = {
   status: '',
   job_position_id: '',
};

const STATUS_OPTIONS = Object.keys(JOB_OPENING_STATUS_LABEL);

function readFiltersFromUrl(searchParams) {
   const next = { ...DEFAULT_FILTERS };
   Object.keys(DEFAULT_FILTERS).forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null && value !== '') next[key] = value;
   });
   return next;
}

function toServiceArgs(filters, page) {
   return {
      page,
      perPage: ITEMS_PER_PAGE,
      status: filters.status,
      jobPositionId: filters.job_position_id,
   };
}

export default function OpeningList() {
   const navigate = useNavigate();
   const { hasPermission } = usePermissions();
   const canCreate = hasPermission('hr:job_opening:create');

   const [searchParams, setSearchParams] = useSearchParams();
   const hasInitialQueryParams = searchParams.toString() !== '';

   const [filters, setFilters] = useState(() => readFiltersFromUrl(searchParams));
   const [appliedFilters, setAppliedFilters] = useState(filters);
   const [currentPage, setCurrentPage] = useState(() => {
      const fromUrl = Number(searchParams.get('page'));
      return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 1;
   });

   const [openings, setOpenings] = useState([]);
   const [totalPages, setTotalPages] = useState(1);
   const [totalCount, setTotalCount] = useState(0);
   const [isLoading, setIsLoading] = useState(false);

   const didInitialFetch = useRef(false);

   const commitToUrl = useCallback(
      (values, page) => {
         const params = { applied: '1' };
         Object.keys(DEFAULT_FILTERS).forEach((key) => {
            if (values[key]) params[key] = values[key];
         });
         if (page > 1) params.page = String(page);
         setSearchParams(params, { replace: true });
      },
      [setSearchParams],
   );

   const fetchOpenings = useCallback(async (values, page) => {
      setIsLoading(true);
      try {
         const result = await listJobOpenings(toServiceArgs(values, page));
         setOpenings(result.jobOpenings);
         setTotalPages(result.totalPages);
         setTotalCount(result.total);
         setCurrentPage(result.page);
      } catch (err) {
         const apiMessage =
            err?.response?.data?.errors?.[0]?.message ??
            err?.response?.data?.message ??
            err?.message ??
            'Erro ao buscar vagas.';
         toast.error(apiMessage);
      } finally {
         setIsLoading(false);
      }
   }, []);

   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      if (!hasInitialQueryParams) return;
      fetchOpenings(appliedFilters, currentPage);
   }, [fetchOpenings, hasInitialQueryParams, appliedFilters, currentPage]);

   const updateField = (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
   };

   const handleSearch = (event) => {
      event.preventDefault();
      setAppliedFilters(filters);
      commitToUrl(filters, 1);
      fetchOpenings(filters, 1);
   };

   const handleClear = () => {
      setFilters(DEFAULT_FILTERS);
      setAppliedFilters(DEFAULT_FILTERS);
      setOpenings([]);
      setTotalCount(0);
      setTotalPages(1);
      setCurrentPage(1);
      setSearchParams({}, { replace: true });
   };

   const handlePageChange = (page) => {
      commitToUrl(appliedFilters, page);
      fetchOpenings(appliedFilters, page);
   };

   const hasResults = openings.length > 0;

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Vagas</PageTitle>
               <PageSubtitle>
                  Aberturas de processo seletivo por cargo
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               {canCreate && (
                  <CtaButton
                     type="button"
                     onClick={() => navigate('/hr/recruitment/openings/new')}
                  >
                     + Nova vaga
                  </CtaButton>
               )}
            </PageHeaderActions>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field className="col-4">
                        <label htmlFor="filter-status">Status</label>
                        <select
                           id="filter-status"
                           value={filters.status}
                           onChange={(e) =>
                              updateField('status', e.target.value)
                           }
                        >
                           <option value="">Todos</option>
                           {STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                 {JOB_OPENING_STATUS_LABEL[status]}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-position">Cargo</label>
                        <JobPositionSelect
                           id="filter-position"
                           value={filters.job_position_id}
                           onChange={(value) =>
                              updateField('job_position_id', value)
                           }
                           placeholder="Todos"
                        />
                     </Field>
                  </FormGrid>

                  <ActionBar style={{ marginTop: 16 }}>
                     <ActionBarLeft />
                     <ActionBarRight>
                        <TextLink
                           as="button"
                           type="button"
                           onClick={handleClear}
                           disabled={isLoading}
                        >
                           Limpar
                        </TextLink>
                        <CtaButton type="submit" disabled={isLoading}>
                           Pesquisar
                        </CtaButton>
                     </ActionBarRight>
                  </ActionBar>
               </form>
            </SectionBody>
         </Section>

         <Section>
            <SectionHeader>
               <SectionTitle>
                  Resultados{totalCount ? ` (${totalCount})` : ''}
               </SectionTitle>
            </SectionHeader>
            <SectionBody>
               {hasResults ? (
                  <>
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th>Vaga</th>
                                 <th>Cargo</th>
                                 <th className="center">Nº vagas</th>
                                 <th className="center">Status</th>
                                 <th>Aberta em</th>
                              </tr>
                           </thead>
                           <tbody>
                              {openings.map((opening) => (
                                 <ClickableRow
                                    key={opening.id}
                                    onClick={() =>
                                       navigate(
                                          `/hr/recruitment/openings/${opening.id}`,
                                       )
                                    }
                                 >
                                    <td>
                                       {opening.title ||
                                          opening.job_position?.position_name ||
                                          '—'}
                                    </td>
                                    <td>
                                       {opening.job_position?.position_name ??
                                          '—'}
                                    </td>
                                    <td className="center">
                                       {opening.openings_count ?? '—'}
                                    </td>
                                    <td className="center">
                                       <JobOpeningStatusBadge
                                          value={opening.status}
                                       />
                                    </td>
                                    <td>{formatDateBr(opening.opened_at)}</td>
                                 </ClickableRow>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                     />
                  </>
               ) : (
                  <EmptyState
                     title="Nenhuma vaga encontrada"
                     description="Ajuste os filtros e clique em Pesquisar, ou cadastre uma nova vaga."
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
